import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import * as Rx from 'rxjs';

export enum StickyBehaviour {
  ALWAYS = 'always',
  ONSCROLL = 'onscroll',
}

export enum StickyPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
}

@Directive({
  selector: `
    [flSticky],
  `,
})
export class StickyDirective implements OnChanges, AfterViewInit, OnDestroy {
  @Input() flSticky = true;
  @Input() flStickyOffset = 0;
  @Input() flStickyBehaviour = StickyBehaviour.ONSCROLL;
  @Input() flStickyPosition = StickyPosition.TOP;
  @Input() flStickyBoundary: ElementRef;

  @Output() activated = new EventEmitter<boolean>();

  private isSticky = false;
  private elementPosition: number;
  private placeholder = this.renderer.createElement('div') as HTMLDivElement;
  private windowResize?: Rx.Subscription;
  private windowScroll?: Rx.Subscription;
  private isElementHidden = false;
  private isBoundaryReached = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private element: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    // don't set up directive on server
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if ('flSticky' in changes && this.element && this.flSticky) {
      this.initializeSticky();
    } else {
      this.deactivate(this.element.nativeElement);
      this.destroyListeners();
    }
  }

  ngAfterViewInit() {
    // don't set up directive on server
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.flSticky) {
      this.initializeSticky();
    }
  }

  initializeSticky() {
    const { nativeElement } = this.element;

    this.elementPosition = this.getElementPosition(nativeElement);

    if (this.flStickyBehaviour === StickyBehaviour.ONSCROLL) {
      if (this.windowScroll === undefined) {
        // FIXME: flSticky needs to be re-written withtout any scroll listener
        // tslint:disable-next-line: no-scroll-listener
        this.windowScroll = Rx.fromEvent(window, 'scroll').subscribe(() => {
          requestAnimationFrame(timestamp => {
            if (this.isElementHidden) {
              this.deactivate(nativeElement);
            } else {
              this.toggleStickyState(nativeElement);
            }
          });
        });
      }
    } else {
      this.activate(nativeElement);
    }

    if (this.windowResize === undefined) {
      this.windowResize = Rx.fromEvent(window, 'resize').subscribe(() => {
        this.isElementHidden =
          window.getComputedStyle(nativeElement).display === 'none';

        if (this.isElementHidden && this.isSticky) {
          this.deactivate(nativeElement);
        } else {
          this.toggleStickyState(nativeElement);
        }

        if (this.isSticky) {
          const placeholderWidth = this.placeholder.getBoundingClientRect()
            .width;
          this.updateWidth(nativeElement, placeholderWidth);
        }
      });
    }
  }

  destroyListeners() {
    if (this.windowScroll) {
      this.windowScroll.unsubscribe();
    }

    if (this.windowResize) {
      this.windowResize.unsubscribe();
    }
  }

  toggleStickyState(element: HTMLElement) {
    if (this.flStickyPosition === StickyPosition.TOP) {
      if (!this.flStickyBoundary) {
        this.toggleStickyTop(element);
      } else {
        this.toggleStickyBoundary(element);
      }
    } else {
      this.toggleStickyBottom(element);
    }
  }

  toggleStickyTop(element: HTMLElement) {
    if (this.flSticky && !this.isSticky) {
      this.elementPosition = this.getElementPosition(element);
      if (window.pageYOffset >= this.elementPosition) {
        this.activate(element);
      }
    } else if (window.pageYOffset < this.elementPosition) {
      this.deactivate(element);
    }
  }

  toggleStickyBottom(element: HTMLElement) {
    const { bottom } = this.placeholder.getBoundingClientRect();

    if (this.flSticky && !this.isSticky) {
      this.elementPosition = this.getElementPosition(element);

      if (window.innerHeight >= this.elementPosition) {
        this.activate(element);
      }
    } else if (bottom > this.elementPosition) {
      this.deactivate(element);
    }
  }

  activate(element: HTMLElement, position = this.flStickyOffset) {
    const { height, width } = element.getBoundingClientRect();
    this.activated.emit(true);
    this.createPlaceholder(element, height);
    this.renderer.setStyle(element, 'position', 'fixed');
    this.renderer.setStyle(element, this.flStickyPosition, `${position}px`);
    this.renderer.setStyle(element, 'z-index', '1000');
    this.renderer.setStyle(element, 'width', `${width}px`);
    this.renderer.addClass(element, 'IsSticky');
    this.isSticky = true;
  }

  deactivate(element: HTMLElement) {
    if (this.isSticky) {
      this.activated.emit(false);
      this.destoryPlaceholder(element);
      this.renderer.removeStyle(element, 'position');
      this.renderer.removeStyle(element, 'top');
      this.renderer.removeStyle(element, 'z-index');
      this.renderer.removeStyle(element, 'width');
      this.renderer.removeClass(element, 'IsSticky');
      this.isSticky = false;
    }
  }

  toggleStickyBoundary(element: HTMLElement) {
    if (this.isOutOfStickyBounds(element)) {
      this.deactivate(element);
    } else {
      this.activateStickToBoundary(element);
    }
  }

  /**
   * As the stick element and boundary element might not
   * always sit in parallel, this method check the offsetTop as well
   */
  isOutOfStickyBounds(element: HTMLElement) {
    const stickyHeight = element.offsetHeight;
    const boundary = this.flStickyBoundary.nativeElement;
    const boundaryHeight = parseInt(boundary.offsetHeight, 10);

    return stickyHeight > boundaryHeight;
  }

  activateStickToBoundary(element: HTMLElement) {
    if (this.flSticky) {
      const stickyElementBottom =
        window.pageYOffset +
        element.getBoundingClientRect().height +
        this.flStickyOffset;
      const boundaryElement = this.flStickyBoundary.nativeElement;
      const boundary =
        parseInt(boundaryElement.offsetTop, 10) +
        parseInt(boundaryElement.offsetHeight, 10);

      /**
       * If scroll position reaches the element's original position
       * Deactivate the stickiness of the element and prevent from running the other code
       */
      if (
        this.isSticky &&
        window.pageYOffset < this.getElementPosition(this.placeholder)
      ) {
        this.removeStickToBottomStyle(element);
        this.deactivate(element);
        return;
      }

      /**
       * If scroll position reaches the top of the element
       * Make element stick to top but stops when it reaches
       * the bottom prevented by isBoundaryReached
       */
      if (
        window.pageYOffset >= this.getElementPosition(element) &&
        !this.isBoundaryReached
      ) {
        this.removeStickToBottomStyle(element);
        if (!this.isSticky) {
          this.activate(element);
        }
      }

      /**
       * If the boundary is reached
       * Make element stick to boundary when bottom of the element reaches the boundary
       * isBoundaryReach is set to true to prevent the element from sticking to top
       */
      if (stickyElementBottom >= boundary && !this.isBoundaryReached) {
        this.isBoundaryReached = true;
        this.isSticky = false;
        this.setStickToBottomStyle(element);
      }

      /**
       * If the boundary is reached and user scrolls back up
       * Stick to the top again
       */
      if (
        element.getBoundingClientRect().top - this.flStickyOffset >= 0 &&
        this.isBoundaryReached
      ) {
        this.removeStickToBottomStyle(element);
        if (!this.isSticky) {
          this.activate(element);
        }
        this.isBoundaryReached = false;
      }
    }
  }

  setStickToBottomStyle(element: HTMLElement) {
    this.renderer.setStyle(element, 'position', 'absolute');
    this.renderer.setStyle(element, 'bottom', '0');
    this.renderer.setStyle(element, 'top', 'auto');
  }

  removeStickToBottomStyle(element: HTMLElement) {
    this.renderer.removeStyle(element, 'bottom');
  }

  getElementPosition(element: HTMLElement) {
    if (this.flStickyPosition === StickyPosition.TOP) {
      return (
        element.getBoundingClientRect().top -
        document.body.getBoundingClientRect().top -
        this.flStickyOffset
      );
    }

    return element.getBoundingClientRect().bottom + this.flStickyOffset;
  }

  updateWidth(element: HTMLElement, width: number) {
    this.renderer.setStyle(element, 'width', `${width}px`);
  }

  /**
   * Placeholder is created to avoid jump when component becomes sticky
   * and serves as the width basis when user resizes screens
   */
  createPlaceholder(element: HTMLElement, height: number) {
    this.placeholder.style.height = `${height}px`;
    this.renderer.appendChild(element.parentNode, this.placeholder);
  }

  destoryPlaceholder(element: HTMLElement) {
    this.renderer.removeChild(element.parentNode, this.placeholder);
  }

  ngOnDestroy() {
    this.destroyListeners();
  }
}
